import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  format,
  isBefore,
  isSameDay,
  startOfDay,
  endOfDay,
  isAfter,
  addMonths,
  parseISO,
} from "date-fns";
import { pl } from "date-fns/locale";
import { createPortal } from "react-dom";
import deleteButtonIcon from "../../media/delete-button.svg";
import orangeSortIcon from "../../media/orange-sort-icon.svg";
import httpClient from "../../services/httpClient";
import { useNotification } from "../../components/NotificationContext";
import { useAuth } from "../../AuthContext";
import eventBus from "../../components/eventBus";
import Logger from "../../utils/Logger";
import "../../styleSheet/main/reservations/carsReservations/carsReservations.scss";

const EditCarReservation = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  // const { showNotification } = useNotification();
  const { userData } = useAuth();

  // Rozdzielone stany dla dat i godzin
  const [departureDate, setDepartureDate] = useState(null);
  const [departureTime, setDepartureTime] = useState(null);
  const [arrivalDate, setArrivalDate] = useState(null);
  const [arrivalTime, setArrivalTime] = useState(null);

  // Połączone daty i godziny
  const [departureDateTime, setDepartureDateTime] = useState(null);
  const [arrivalDateTime, setArrivalDateTime] = useState(null);

  const [city, setCity] = useState("");
  const [tripPurpose, setTripPurpose] = useState("");
  const [selectedCar, setSelectedCar] = useState(null);
  const [errors, setErrors] = useState({});
  const [hoveredIcon, setHoveredIcon] = useState(false);
  const [isDatesSet, setIsDatesSet] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState({});
  const [cars, setCars] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReservation, setIsLoadingReservation] = useState(true);
  const [originalReservation, setOriginalReservation] = useState(null);

  const getEmail = () => userData.userEmail;
  const getName = () => userData.userName;

  // Funkcja do łączenia daty i godziny
  const combineDateAndTime = (date, time) => {
    if (!date || !time) return null;

    const combined = new Date(date);
    combined.setHours(time.getHours());
    combined.setMinutes(time.getMinutes());
    combined.setSeconds(0);
    combined.setMilliseconds(0);

    return combined;
  };

  // Aktualizuj połączone daty gdy zmieniają się osobne pola
  useEffect(() => {
    const newDepartureDateTime = combineDateAndTime(
      departureDate,
      departureTime,
    );
    const newArrivalDateTime = combineDateAndTime(arrivalDate, arrivalTime);

    setDepartureDateTime(newDepartureDateTime);
    setArrivalDateTime(newArrivalDateTime);
  }, [departureDate, departureTime, arrivalDate, arrivalTime]);

  // Pobierz dane istniejącej rezerwacji
  useEffect(() => {
    const fetchReservation = async () => {
      try {
        setIsLoadingReservation(true);
        const response = await httpClient.get(`/CarReservations/${id}`, {
          withAuth: true,
        });

        const reservation = response.data;
        setOriginalReservation(reservation);

        // Ustaw dane w formularzu
        const fromDate = new Date(reservation.startDateLocal);
        const toDate = new Date(reservation.endDateLocal);

        // Ustaw daty (bez godzin)
        setDepartureDate(
          new Date(
            fromDate.getFullYear(),
            fromDate.getMonth(),
            fromDate.getDate(),
          ),
        );
        setArrivalDate(
          new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate()),
        );

        // Ustaw godziny
        setDepartureTime(fromDate);
        setArrivalTime(toDate);

        setCity(reservation.location || "");
        setTripPurpose(reservation.reason || "");

        // Ustaw wybrany samochód
        if (reservation.car) {
          setSelectedCar({
            id: reservation.car.id,
            make: reservation.car.make,
            model: reservation.car.model,
            licensePlate: reservation.car.licensePlate,
          });
        }

        // Sprawdź czy użytkownik ma uprawnienia do edycji tej rezerwacji
        // Tylko twórca rezerwacji lub administrator może edytować
        const isCreator = reservation.user?.email === userData.userEmail;
        const isAdmin = userData.userRole === "admin";

        if (!isCreator && !isAdmin) {
          Logger.warn(
            "Brak uprawnień do edycji tej rezerwacji. Użytkownik:",
            userData.userEmail,
            "Twórca:",
            reservation.user?.email,
          );
          alert(
            "Nie masz uprawnień do edycji tej rezerwacji. Tylko twórca rezerwacji lub administrator może ją edytować.",
          );
          navigate("/rezerwacje/samochody/");
          return;
        }
      } catch (error) {
        Logger.error("Błąd podczas pobierania rezerwacji:", error);
        // showNotification("Nie udało się załadować danych rezerwacji.", "error");
        navigate("/rezerwacje/samochody/");
      } finally {
        setIsLoadingReservation(false);
      }
    };

    if (id) {
      fetchReservation();
    }
  }, [id, navigate, userData]);

  // Pobierz dostępne samochody gdy zmienią się daty
  useEffect(() => {
    const fetchCars = async () => {
      if (!departureDateTime || !arrivalDateTime) {
        return;
      }

      try {
        const startDate = format(departureDateTime, "yyyy-MM-dd'T'HH:mm:ss");
        const endDate = format(arrivalDateTime, "yyyy-MM-dd'T'HH:mm:ss");

        const response = await httpClient.get(
          `cars/available-cars?startdatelocal=${encodeURIComponent(
            startDate,
          )}&enddatelocal=${encodeURIComponent(endDate)}&exceptReservationId=${id}`,
          { withAuth: true },
        );

        const data = response.data;

        if (Array.isArray(data)) {
          setCars(data);
          setIsDatesSet(true);
        }
      } catch (error) {
        Logger.error("Błąd podczas pobierania samochodów:", error);
      }
    };

    fetchCars();
  }, [departureDateTime, arrivalDateTime, originalReservation, id]);

  const validateDates = (departure, arrival) => {
    if (!departure || !arrival) return false;
    return isBefore(departure, arrival);
  };

  const handleCarSelection = (car) => {
    // Check if this is the current car from the reservation
    const isCurrentCar =
      originalReservation &&
      originalReservation.car &&
      car.make === originalReservation.car.make &&
      car.model === originalReservation.car.model &&
      car.licensePlate === originalReservation.car.licensePlate;

    // Don't allow selection if car is not available (unless it's the current car)
    if (car.isAvailable === false && !isCurrentCar) {
      return;
    }

    // Compare cars by their properties instead of reference
    const isSameCar =
      selectedCar &&
      selectedCar.make === car.make &&
      selectedCar.model === car.model &&
      selectedCar.licensePlate === car.licensePlate;

    setSelectedCar(isSameCar ? null : car);
  };

  const handleDepartureDateChange = (date) => {
    setDepartureDate(date);
  };

  const handleDepartureTimeChange = (time) => {
    setDepartureTime(time);
  };

  const handleArrivalDateChange = (date) => {
    setArrivalDate(date);
  };

  const handleArrivalTimeChange = (time) => {
    setArrivalTime(time);
  };

  const handleUpdateReservation = async () => {
    const newErrors = {};

    if (!departureDate) {
      newErrors.departureDate = t(
        "reservations.cars.errors.selectDepartureDate",
      );
    }
    if (!departureTime) {
      newErrors.departureTime = t(
        "reservations.cars.errors.selectDepartureTime",
      );
    }
    if (!arrivalDate) {
      newErrors.arrivalDate = t("reservations.cars.errors.selectArrivalDate");
    }
    if (!arrivalTime) {
      newErrors.arrivalTime = t("reservations.cars.errors.selectArrivalTime");
    }

    if (departureDateTime && arrivalDateTime) {
      if (!validateDates(departureDateTime, arrivalDateTime)) {
        newErrors.arrivalDateTime = t(
          "reservations.cars.errors.invalidDateRange",
        );
      }
    }

    if (!city) {
      newErrors.city = t("reservations.cars.errors.enterCity");
    }
    if (!tripPurpose) {
      newErrors.tripPurpose = t("reservations.cars.errors.enterPurpose");
    }
    if (!selectedCar) {
      newErrors.selectedCar = t("reservations.cars.errors.selectCar");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      setConfirmationData({
        car: `${selectedCar.make} ${selectedCar.model} - ${selectedCar.licensePlate}`,
        departureDate: format(departureDateTime, "dd-MM-yyyy HH:mm"),
        arrivalDate: format(arrivalDateTime, "dd-MM-yyyy HH:mm"),
        city,
        tripPurpose,
      });

      setShowConfirmation(true);
    }
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    const reservationData = {
      carId: selectedCar.id,
      startDateLocal: departureDateTime.toISOString(),
      endDateLocal: arrivalDateTime.toISOString(),
      location: city,
      reason: tripPurpose,
    };

    // Validate that carId is present
    if (!reservationData.carId) {
      // showNotification("Błąd: Nie można określić ID samochodu.", "error");
      setIsLoading(false);
      return;
    }

    try {
      await httpClient.put(`/CarReservations/${id}`, reservationData, {
        withAuth: true,
      });

      // showNotification(
      //   "Rezerwacja została zaktualizowana pomyślnie!",
      //   "success"
      // );
      setShowConfirmation(false);
      eventBus.emit("car-reservation-updated", {});
      navigate("/rezerwacje/samochody/");
    } catch (error) {
      Logger.error("Błąd podczas aktualizacji rezerwacji:", error);
      Logger.error("Error response:", error.response?.data);
      Logger.error("Error status:", error.response?.status);

      // Sprawdź czy to błąd autoryzacji
      const isAuthError =
        error.response?.status === 403 ||
        error.response?.status === 401 ||
        (error.response?.status === 400 &&
          error.response?.data?.some?.(
            (err) =>
              err.message?.toLowerCase().includes("not authorized") ||
              err.message?.toLowerCase().includes("authorization"),
          ));

      if (isAuthError) {
        alert(
          "Brak uprawnień do edycji tej rezerwacji.\n\n" +
            "Tylko twórca rezerwacji lub administrator może ją edytować.\n\n" +
            "Jeśli jesteś administratorem, skontaktuj się z zespołem IT - " +
            "backend wymaga aktualizacji uprawnień.\n\n" +
            "Zobacz plik ADMIN_EDIT_RESERVATIONS.md w katalogu głównym projektu.",
        );
        navigate("/rezerwacje/samochody/");
        return;
      }

      // Wyświetl ogólny błąd
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Wystąpił błąd podczas aktualizacji rezerwacji.";
      alert(`Błąd podczas aktualizacji rezerwacji:\n${errorMessage}`);
      // showNotification(
      //   "Wystąpił błąd podczas aktualizacji rezerwacji.",
      //   "error"
      // );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleCancelChange = () => {
    navigate("/rezerwacje/samochody/");
  };

  const handleIconClick = () => {
    navigate("/rezerwacje/samochody");
  };

  const renderConfirmationModal = () => {
    if (!showConfirmation) return null;

    return createPortal(
      <div className="confirmation-overlay">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={handleCancel}
          />
          <p>{t("reservations.cars.confirmUpdate")}</p>
          <div className="confirmation-details">
            <p>
              <strong>{t("reservations.cars.car")} : </strong>{" "}
              {confirmationData.car}
            </p>
            <p>
              <strong>{t("reservations.cars.departureDateTime")} : </strong>{" "}
              {confirmationData.departureDate}
            </p>
            <p>
              <strong>{t("reservations.cars.arrivalDateTime")} : </strong>{" "}
              {confirmationData.arrivalDate}
            </p>
            <p>
              <strong>{t("reservations.cars.city")} : </strong>{" "}
              {confirmationData.city}
            </p>
            <p>
              <strong>{t("reservations.cars.tripPurpose")} : </strong>{" "}
              {confirmationData.tripPurpose}
            </p>
          </div>
          <div className="confirmation-buttons">
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t("reservations.cars.updatingReservation")}</p>
              </div>
            ) : (
              <>
                <button onClick={handleConfirm}>{t("common.yes")}</button>
                <button onClick={handleCancelChange}>{t("common.no")}</button>
              </>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  const decideMinTimeForDeparture = () => {
    if (!departureDate) return startOfDay(new Date());
    if (isSameDay(departureDate, new Date())) {
      const now = new Date();
      now.setSeconds(0);
      now.setMilliseconds(0);
      return now;
    }
    return startOfDay(departureDate);
  };

  const decideMinTimeForArrival = () => {
    if (!arrivalDate) return startOfDay(new Date());
    if (isSameDay(arrivalDate, departureDate) && departureTime) {
      return departureTime;
    }
    if (isSameDay(arrivalDate, new Date())) {
      const now = new Date();
      now.setSeconds(0);
      now.setMilliseconds(0);
      return now;
    }
    return startOfDay(arrivalDate);
  };

  if (isLoadingReservation) {
    return (
      <div className="edit-car-reservation-loading-container">
        <div className="edit-car-reservation-loading-spinner"></div>
        <p>{t("reservations.cars.loadingReservation")}</p>
      </div>
    );
  }

  return (
    <div className="edit-car-reservation-container">
      <div
        className="edit-car-reservation-main-site"
        style={{ justifyContent: "space-between" }}
      >
        <h1>{t("reservations.cars.editReservation")}</h1>
        <div
          className="edit-car-reservation-orange-sort-icon"
          onClick={handleIconClick}
          onMouseEnter={() => setHoveredIcon(true)}
          onMouseLeave={() => setHoveredIcon(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hoveredIcon ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>
      {isDatesSet && (
        <div className="edit-car-reservation-reservation-details">
          <div className="edit-car-reservation-date-inputs">
            <div className="edit-car-reservation-the-first-line">
              <div className="edit-car-reservation-input-group">
                <div className="edit-car-reservation-date-time-container">
                  <div className="edit-car-reservation-date-picker-container">
                    <label htmlFor="departureDate">
                      {t("reservations.cars.departureDate")}
                    </label>
                    <DatePicker
                      selected={departureDate}
                      onChange={handleDepartureDateChange}
                      dateFormat="dd-MM-yyyy"
                      locale={pl}
                      placeholderText={t(
                        "reservations.cars.selectDepartureDate",
                      )}
                      minDate={new Date()}
                      maxDate={addMonths(new Date(), 6)}
                    />
                    {errors.departureDate && (
                      <span className="edit-car-reservation-error-message">
                        {errors.departureDate}
                      </span>
                    )}
                  </div>

                  <div className="edit-car-reservation-time-picker-container">
                    <label htmlFor="departureTime">
                      {t("reservations.cars.departureTime")}
                    </label>
                    <DatePicker
                      selected={departureTime}
                      onChange={handleDepartureTimeChange}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption={t("reservations.common.time")}
                      dateFormat="HH:mm"
                      locale={pl}
                      placeholderText={t(
                        "reservations.cars.selectDepartureTime",
                      )}
                      minTime={decideMinTimeForDeparture()}
                      maxTime={endOfDay(new Date())}
                      disabled={!departureDate}
                    />
                    {errors.departureTime && (
                      <span className="edit-car-reservation-error-message">
                        {errors.departureTime}
                      </span>
                    )}
                  </div>

                  <div className="edit-car-reservation-date-picker-container">
                    <label htmlFor="arrivalDate">
                      {t("reservations.cars.arrivalDate")}
                    </label>
                    <DatePicker
                      selected={arrivalDate}
                      onChange={handleArrivalDateChange}
                      dateFormat="dd-MM-yyyy"
                      locale={pl}
                      placeholderText={t("reservations.cars.selectArrivalDate")}
                      minDate={departureDate || new Date()}
                      maxDate={addMonths(new Date(), 6)}
                    />
                    {errors.arrivalDate && (
                      <span className="edit-car-reservation-error-message">
                        {errors.arrivalDate}
                      </span>
                    )}
                  </div>

                  <div className="edit-car-reservation-time-picker-container">
                    <label htmlFor="arrivalTime">
                      {t("reservations.cars.arrivalTime")}
                    </label>
                    <DatePicker
                      selected={arrivalTime}
                      onChange={handleArrivalTimeChange}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption={t("reservations.common.time")}
                      dateFormat="HH:mm"
                      locale={pl}
                      placeholderText={t("reservations.cars.selectArrivalTime")}
                      minTime={decideMinTimeForArrival()}
                      maxTime={endOfDay(new Date())}
                      disabled={!arrivalDate}
                    />
                    {errors.arrivalTime && (
                      <span className="edit-car-reservation-error-message">
                        {errors.arrivalTime}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="edit-car-reservation-available-group">
            <label>{t("reservations.cars.availableCars")}</label>
            <div
              className="edit-car-reservation-cars-list"
              style={{ marginTop: "2rem" }}
            >
              {cars.map((car) => {
                // Check if this is the current car from the reservation
                const isCurrentCar =
                  originalReservation &&
                  originalReservation.car &&
                  car.make === originalReservation.car.make &&
                  car.model === originalReservation.car.model &&
                  car.licensePlate === originalReservation.car.licensePlate;

                // Disable only if unavailable AND not the current car
                const isDisabled = car.isAvailable === false && !isCurrentCar;
                const isSelected =
                  selectedCar &&
                  selectedCar.make === car.make &&
                  selectedCar.model === car.model &&
                  selectedCar.licensePlate === car.licensePlate;

                return (
                  <div
                    key={car.id}
                    className={`edit-car-reservation-car-item ${
                      isDisabled ? "disabled" : ""
                    }`}
                    onClick={() => handleCarSelection(car)}
                    style={{
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <span
                      className={`edit-car-reservation-car-checkbox ${
                        isSelected && !isDisabled ? "checked" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDisabled) {
                          handleCarSelection(car);
                        }
                      }}
                    />
                    {car.make} {car.model} - {car.licensePlate}
                    {isCurrentCar && (
                      <span
                        style={{
                          color: "#ff9900",
                          fontSize: "0.9em",
                          marginLeft: "10px",
                        }}
                      >
                        ({t("reservations.cars.currentCar")})
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {errors.selectedCar && (
              <span className="edit-car-reservation-error-message">
                {errors.selectedCar}
              </span>
            )}
          </div>

          <div className="edit-car-reservation-reservation-details-input">
            <div className="edit-car-reservation-input-group">
              <label htmlFor="city">{t("reservations.cars.city")}</label>
              <input
                type="text"
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t("reservations.cars.enterCity")}
              />
              {errors.city && (
                <span className="edit-car-reservation-error-down-message">
                  {errors.city}
                </span>
              )}
            </div>

            <div className="edit-car-reservation-input-group">
              <label htmlFor="tripPurpose">
                {t("reservations.cars.purpose")}
              </label>
              <textarea
                id="tripPurpose"
                value={tripPurpose}
                onChange={(e) => setTripPurpose(e.target.value)}
                placeholder={t("reservations.cars.enterPurpose")}
              />
              {errors.tripPurpose && (
                <span className="edit-car-reservation-error-down-message">
                  {errors.tripPurpose}
                </span>
              )}
            </div>

            <button
              className="edit-car-reservation-reservations-button"
              onClick={handleUpdateReservation}
            >
              {t("reservations.cars.updateReservation")}
            </button>
          </div>
        </div>
      )}

      {renderConfirmationModal()}
    </div>
  );
};

export default EditCarReservation;
